import {
  AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone,
  OnChanges, OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { SimplebarAngularComponent } from 'simplebar-angular';
import { formatDayLabel, isSameDay, messageKey, normalizeSearch, toDate } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatMessage, ChatMessageFile, ChatParticipant, ChatVisitAction, ChatCardAction } from '../../chat.model';
import { ChatImageOpen } from '../chat-message/chat-message.component';

export interface ChatThreadItem {
  type: 'day' | 'unread' | 'message';
  key: string;
  label?: string;
  message?: ChatMessage;
  mine?: boolean;
  showAuthor?: boolean;
  groupStart?: boolean;
  groupEnd?: boolean;
  avatarSrc?: string;
  avatarName?: string;
  /** Group conversation: the avatar shows the sender's name on hover. */
  avatarTip?: string;
  /** Matches the in-conversation search (`highlight`). */
  hit?: boolean;
}

/** Two messages of the same author closer than this form a run (grouped bubbles). */
const GROUP_WINDOW_MS = 5 * 60 * 1000;
/** Being this close to the bottom still counts as "reading the latest". */
const BOTTOM_THRESHOLD_PX = 48;

/**
 * The scrolling list of one conversation, inside `ngx-simplebar` (the
 * theme's scrollbar):
 *  - day separators between messages of different days;
 *  - a "Não lidas" separator above the first message the user had not read
 *    when the conversation was opened (fixed for that visit);
 *  - runs of the same author within 5 minutes are grouped (bubbles closer,
 *    author / avatar only on the first, time only on the last);
 *  - the view follows the newest message when the user is at the bottom or
 *    when the message is their own; otherwise a "↓ N novas mensagens" button
 *    appears and the position is kept;
 *  - opening a conversation jumps to the unread separator (or to the end);
 *  - a skeleton while `loading` with no messages yet, and a typing bubble
 *    while `typing`;
 *  - `highlight` marks the messages whose text contains the term and
 *    `focusKey` scrolls one of them into view (search row of the container;
 *    keys come from `messageKey`, shared with the container).
 */
@Component({
  selector: 'chat-thread',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.css']
})
export class ChatThreadComponent implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {
  @Input() messages: ChatMessage[] = [];
  /** UserApp.id of the logged user: decides which bubbles are "mine". */
  @Input() meId?: string;
  /** UserApp.id of the other side; any other sender gets its name shown. */
  @Input() otherId?: string;
  /** Photo / name of the other side, for the avatar next to received bubbles. */
  @Input() otherPhoto?: string;
  @Input() otherName?: string;
  /**
   * Everyone in the conversation (2026-10-26). With more than two people each
   * received run gets its sender's photo, with the name in a tooltip.
   * `profile` is a file name of `/users/files/`, resolved with `profileBase`.
   */
  @Input() participants: ChatParticipant[] = [];
  @Input() profileBase = '';
  /** Changes when another conversation is opened: scrolls without animation. */
  @Input() conversationKey: string | null = null;
  /** History still loading (skeleton while there is nothing to show). */
  @Input() loading = false;
  /** The other side is typing: a small bubble with three dots at the end. */
  @Input() typing = false;
  /** In-conversation search term (messages containing it get a soft highlight). */
  @Input() highlight = '';
  /** Key (`messageKey`) of the search hit to bring into view. */
  @Input() focusKey: string | null = null;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() retry = new EventEmitter<ChatMessage>();
  @Output() deleteMessage = new EventEmitter<ChatMessage>();
  @Output() deleteFile = new EventEmitter<{ message: ChatMessage; file: ChatMessageFile }>();
  @Output() openImage = new EventEmitter<ChatImageOpen>();
  /** 7.3: o staff da conversa pode agir nos cartões de visita. */
  @Input() canManageVisits = false;
  /** Staff com `canQualify` numa conversa de cliente: edita e usa a qualificação (7.3). */
  @Input() canQualify = false;
  /** O cartão cuja ação de um clique está a correr (spinner no botão). */
  @Input() actionBusy: string | null = null;
  /** Secção 9: janelas de editar / apagar para todos (segundos). */
  @Input() editWindowSeconds = 0;
  @Input() deleteWindowSeconds = 0;
  @Output() editMessage = new EventEmitter<ChatMessage>();
  /** Secção 10: responder (botão / arrastar) e ir para a mensagem citada. */
  @Input() canReply = false;
  @Output() reply = new EventEmitter<ChatMessage>();
  @Output() jumpTo = new EventEmitter<string>();
  /** Secção 12: reagir a uma mensagem. */
  @Output() react = new EventEmitter<{ message: ChatMessage; emoji: string | null }>();
  @Output() visitAction = new EventEmitter<ChatVisitAction>();
  @Output() cardAction = new EventEmitter<ChatCardAction>();
  @Input() dealId?: number | null;

  @ViewChild('bar', { static: true }) bar!: SimplebarAngularComponent;
  @ViewChild('fallback', { static: true }) fallback!: ElementRef<HTMLElement>;

  items: ChatThreadItem[] = [];
  /** Messages that arrived below the fold while the user was reading above. */
  unseen = 0;
  atBottom = true;
  readonly skeleton = [
    { out: false, w: 220 }, { out: true, w: 260 }, { out: true, w: 140 }, { out: false, w: 300 }, { out: false, w: 180 }, { out: true, w: 240 }
  ];

  private pendingScroll: 'none' | 'jump' | 'smooth' = 'none';
  private pendingFocus: string | null = null;
  private lastCount = 0;
  /** Key of the first unread received message at open time; undefined = not decided yet, null = none. */
  private unreadAnchor: string | null | undefined;
  private scrollEl: HTMLElement | null = null;
  private onScroll = () => this.checkBottom();

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationKey'] && !changes['conversationKey'].firstChange) {
      this.pendingScroll = 'jump';
      this.lastCount = 0;
      this.unreadAnchor = undefined;
      this.unseen = 0;
      this.atBottom = true;
    }
    if (changes['messages'] || changes['meId'] || changes['otherId'] || changes['otherPhoto'] || changes['strings'] || changes['highlight']
      || changes['participants'] || changes['profileBase']) {
      const list = this.messages || [];
      if (this.unreadAnchor === undefined && list.length > 0) {
        this.unreadAnchor = this.findUnreadAnchor(list);
      }
      this.items = this.build(list);
      const count = list.length;
      if (count > this.lastCount) {
        const last = list[count - 1];
        const mine = !!last && !!last.userSender && last.userSender.id === this.meId;
        if (this.pendingScroll === 'jump' || this.lastCount === 0) {
          this.pendingScroll = 'jump';
        } else if (mine || this.atBottom) {
          this.pendingScroll = 'smooth';
        } else {
          this.unseen += count - this.lastCount;
        }
      }
      this.lastCount = count;
    }
    if (changes['typing'] && this.typing && this.atBottom && this.pendingScroll === 'none') {
      this.pendingScroll = 'smooth';
    }
    if (changes['focusKey'] && this.focusKey) {
      this.pendingFocus = this.focusKey;
    }
  }

  ngAfterViewInit(): void {
    this.scrollEl = this.resolveScrollElement();
    if (this.scrollEl) {
      this.zone.runOutsideAngular(() => this.scrollEl!.addEventListener('scroll', this.onScroll, { passive: true }));
    }
  }

  ngAfterViewChecked(): void {
    if (this.pendingFocus) {
      const key = this.pendingFocus;
      this.pendingFocus = null;
      this.pendingScroll = 'none';
      setTimeout(() => this.scrollToKey(key), 30);
      return;
    }
    if (this.pendingScroll === 'none') {
      return;
    }
    const mode = this.pendingScroll;
    this.pendingScroll = 'none';
    // Images and cards settle their height a tick later.
    setTimeout(() => this.scrollToLatest(mode === 'smooth'), 50);
  }

  ngOnDestroy(): void {
    if (this.scrollEl) {
      this.scrollEl.removeEventListener('scroll', this.onScroll);
    }
  }

  /** "↓ novas mensagens": smooth scroll to the end. */
  jumpToLatest(): void {
    this.unseen = 0;
    this.scrollToLatest(true);
  }

  get newMessagesLabel(): string {
    if (this.unseen === 1) {
      return this.strings.newMessages;
    }
    return this.unseen > 1 ? this.strings.newMessagesCount.replace('{n}', String(this.unseen)) : this.strings.scrollToLatest;
  }

  trackByKey = (_i: number, item: ChatThreadItem) => item.key;

  /* ---- scroll ---- */

  private resolveScrollElement(): HTMLElement | null {
    const instance = this.bar && this.bar.SimpleBar;
    if (instance && typeof instance.getScrollElement === 'function') {
      const el = instance.getScrollElement();
      if (el) {
        return el as HTMLElement;
      }
    }
    return this.fallback ? this.fallback.nativeElement : null;
  }

  private scrollToLatest(smooth: boolean): void {
    const el = this.scrollEl || this.resolveScrollElement();
    if (!el) {
      return;
    }
    this.scrollEl = el;
    // Opening a conversation with unread messages: land on the separator.
    const anchor = !smooth && this.unreadAnchor ? el.querySelector<HTMLElement>('[data-unread]') : null;
    if (anchor) {
      this.scrollIntoScroller(el, anchor, 12);
      this.checkBottom();
      return;
    }
    if (smooth && typeof el.scrollTo === 'function' && !prefersReducedMotion()) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    this.atBottom = true;
    this.unseen = 0;
    this.cdr.markForCheck();
  }

  /** Search: bring the message with `key` to the upper third of the view. */
  private scrollToKey(key: string): void {
    const el = this.scrollEl || this.resolveScrollElement();
    if (!el) {
      return;
    }
    const target = el.querySelector<HTMLElement>('[data-key="' + key.replace(/"/g, '') + '"]');
    if (target) {
      this.scrollIntoScroller(el, target, Math.max(24, Math.round(el.clientHeight / 3)));
      this.checkBottom();
    }
  }

  private scrollIntoScroller(el: HTMLElement, target: HTMLElement, offset: number): void {
    const delta = target.getBoundingClientRect().top - el.getBoundingClientRect().top - offset;
    const top = Math.max(0, el.scrollTop + delta);
    if (typeof el.scrollTo === 'function' && !prefersReducedMotion()) {
      el.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollTop = top;
    }
  }

  private checkBottom(): void {
    const el = this.scrollEl;
    if (!el) {
      return;
    }
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
    if (atBottom !== this.atBottom || (atBottom && this.unseen > 0)) {
      this.zone.run(() => {
        this.atBottom = atBottom;
        if (atBottom) {
          this.unseen = 0;
        }
        this.cdr.markForCheck();
      });
    }
  }

  /* ---- items ---- */

  /** The first received message still unread when the conversation was opened (the server has just been told we read them). */
  private findUnreadAnchor(messages: ChatMessage[]): string | null {
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      const received = !!m.userSender && m.userSender.id !== this.meId;
      if (received && m.unread === true && (m.kind || 'text') !== 'system' && i > 0) {
        return messageKey(m, i);
      }
    }
    return null;
  }

  private build(messages: ChatMessage[]): ChatThreadItem[] {
    const out: ChatThreadItem[] = [];
    const needle = normalizeSearch(this.highlight.trim());
    let lastDay: Date | null = null;
    let prev: ChatMessage | null = null;
    let prevItem: ChatThreadItem | null = null;
    const group = this.isGroup(messages);
    messages.forEach((m, index) => {
      const d = toDate(m.time);
      let dayBreak = false;
      if (d && (!lastDay || !isSameDay(lastDay, d))) {
        out.push({ type: 'day', key: 'day:' + d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate(), label: formatDayLabel(d, this.strings) });
        lastDay = d;
        dayBreak = true;
      }
      const key = messageKey(m, index);
      if (this.unreadAnchor && key === this.unreadAnchor) {
        out.push({ type: 'unread', key: 'unread', label: this.strings.unreadSeparator });
        dayBreak = true;
      }
      const senderId = m.userSender ? m.userSender.id : undefined;
      const mine = !!this.meId && senderId === this.meId;
      const kind = m.kind || 'text';
      const groupable = kind !== 'system' && kind !== 'note';
      const sameRun = groupable && !dayBreak && !!prev && !!prevItem && prevItem.type === 'message'
        && (prev.kind || 'text') !== 'system' && (prev.kind || 'text') !== 'note'
        && !!prev.userSender && prev.userSender.id === senderId
        && closeInTime(prev.time, m.time);
      if (sameRun && prevItem) {
        prevItem.groupEnd = false;
      }
      const item: ChatThreadItem = {
        type: 'message',
        key,
        message: m,
        mine,
        showAuthor: !mine && !!senderId && !!this.otherId && senderId !== this.otherId,
        groupStart: !sameRun,
        groupEnd: true,
        avatarSrc: !mine ? this.avatarFor(m, senderId) : undefined,
        avatarName: !mine ? this.nameFor(m, senderId) : undefined,
        avatarTip: !mine && group ? this.nameFor(m, senderId) : undefined,
        hit: !!needle && normalizeSearch(m.message || '').indexOf(needle) > -1
      };
      out.push(item);
      prev = m;
      prevItem = item;
    });
    return out;
  }

  /**
   * More than two people: participants, both sides and anyone who wrote
   * (older conversations may have no participant rows). System notes and
   * internal notes do not count.
   */
  private isGroup(messages: ChatMessage[]): boolean {
    const ids = new Set<string>();
    [this.meId, this.otherId].forEach(id => id && ids.add(String(id)));
    (this.participants || []).forEach(p => p && p.id && ids.add(String(p.id)));
    messages.forEach(m => {
      const kind = m.kind || 'text';
      if (kind !== 'system' && kind !== 'note' && m.userSender && m.userSender.id) {
        ids.add(String(m.userSender.id));
      }
    });
    return ids.size > 2;
  }

  private participant(senderId?: string): ChatParticipant | undefined {
    return senderId ? (this.participants || []).find(p => !!p && String(p.id) === String(senderId)) : undefined;
  }

  private nameFor(m: ChatMessage, senderId?: string): string | undefined {
    const p = this.participant(senderId);
    return (m.userSender && m.userSender.name) || (p && p.name)
      || (senderId && senderId === this.otherId ? this.otherName : undefined);
  }

  /**
   * The other side's photo for its own messages; a colleague's from the
   * message or the participants, as a URL or a `/users/files/` file name.
   */
  private avatarFor(m: ChatMessage, senderId?: string): string | undefined {
    if (senderId && this.otherId && senderId === this.otherId) {
      return this.otherPhoto;
    }
    const p = this.participant(senderId);
    const profile = (m.userSender && m.userSender.profile) || (p && p.profile);
    if (typeof profile !== 'string' || !profile) {
      return undefined;
    }
    if (/^(https?:)?\//.test(profile) || profile.indexOf('data:') === 0) {
      return profile;
    }
    return this.profileBase ? this.profileBase + profile : undefined;
  }
}

function closeInTime(a: any, b: any): boolean {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) {
    return true;
  }
  return Math.abs(db.getTime() - da.getTime()) <= GROUP_WINDOW_MS;
}

function prefersReducedMotion(): boolean {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}
